import { useMemo } from "react";
import { TIER_ORDER, TIER_COLORS, DAYS } from "../utils/tiers";

// A read-only analytics panel for the Roster tab: surfaces player-base trends
// that aren't obvious from the card list — tier balance, when players are
// actually available, how availability differs by tier, junior ages, and a few
// headline numbers. All computed from the passed-in players; no external chart
// library, just Tailwind bars so it matches the rest of the app.

// A stable ordering for the availability time-of-day buckets. Anything not
// listed falls to the end (keeps unexpected/legacy slot labels visible).
const SLOT_ORDER = [
  "6:00 AM–7:30 AM",
  "7:30 AM–9:00 AM",
  "9:00 AM–10:30 AM",
  "10:30 AM–12:00 PM",
  "12:00 PM–1:30 PM",
  "1:30 PM–3:00 PM",
  "3:00 PM–4:30 PM",
  "4:30 PM–6:00 PM",
  "6:00 PM–7:30 PM",
  "7:30 PM–9:00 PM",
];

function slotSort(a, b) {
  const ia = SLOT_ORDER.indexOf(a);
  const ib = SLOT_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
      <div className="text-2xl font-bold text-gray-800 leading-none">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mb-3 mt-0.5">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  );
}

export default function PlayerStatsPanel({ players }) {
  const stats = useMemo(() => {
    const total = players.length;

    // Tier distribution (skip the "No Group" pseudo-tier — it isn't a real
    // player tier, and unassigned status is tracked separately below).
    const realTiers = TIER_ORDER.filter(t => t !== "No Group");
    const tierCounts = {};
    realTiers.forEach(t => { tierCounts[t] = 0; });
    let untiered = 0;
    players.forEach(p => {
      if (tierCounts[p.tier] !== undefined) tierCounts[p.tier]++;
      else untiered++;
    });

    // Classification split.
    const classCounts = {};
    players.forEach(p => {
      const c = p.classification || "Unknown";
      classCounts[c] = (classCounts[c] || 0) + 1;
    });

    // Assigned vs. unassigned.
    const assigned = players.filter(p => p.groupId).length;

    // Availability: by day, by time slot, and by tier x slot.
    const dayCounts = {};
    DAYS.forEach(d => { dayCounts[d] = 0; });
    const slotCounts = {};
    const tierSlot = {}; // tier -> slot -> count
    realTiers.forEach(t => { tierSlot[t] = {}; });
    let totalSlots = 0;
    let noAvail = 0;

    players.forEach(p => {
      const av = p.availability || [];
      if (av.length === 0) noAvail++;
      totalSlots += av.length;
      av.forEach(entry => {
        const [day, slot] = entry.includes("|") ? entry.split("|") : [null, entry];
        if (day && dayCounts[day] !== undefined) dayCounts[day]++;
        if (slot) {
          slotCounts[slot] = (slotCounts[slot] || 0) + 1;
          if (tierSlot[p.tier]) tierSlot[p.tier][slot] = (tierSlot[p.tier][slot] || 0) + 1;
        }
      });
    });

    const slotLabels = Object.keys(slotCounts).sort(slotSort);
    const busiestSlot = slotLabels.reduce(
      (best, s) => (slotCounts[s] > (slotCounts[best] || 0) ? s : best),
      slotLabels[0]
    );

    // Junior ages.
    const juniorAges = players
      .filter(p => p.classification === "Junior" && typeof p.age === "number")
      .map(p => p.age);
    const ageBuckets = { "5–7": 0, "8–10": 0, "11–13": 0, "14–17": 0, "18+": 0 };
    juniorAges.forEach(a => {
      if (a <= 7) ageBuckets["5–7"]++;
      else if (a <= 10) ageBuckets["8–10"]++;
      else if (a <= 13) ageBuckets["11–13"]++;
      else if (a <= 17) ageBuckets["14–17"]++;
      else ageBuckets["18+"]++;
    });

    return {
      total, realTiers, tierCounts, untiered, classCounts, assigned,
      dayCounts, slotCounts, slotLabels, busiestSlot, tierSlot,
      avgSlots: total ? totalSlots / total : 0, noAvail, ageBuckets, juniorCount: juniorAges.length,
    };
  }, [players]);

  if (stats.total === 0) {
    return (
      <div className="text-center text-gray-400 py-16 text-sm">
        No players yet — add or upload players to see insights.
      </div>
    );
  }

  const maxTier = Math.max(1, ...stats.realTiers.map(t => stats.tierCounts[t]));
  const maxDay = Math.max(1, ...DAYS.map(d => stats.dayCounts[d]));
  const maxSlot = Math.max(1, ...stats.slotLabels.map(s => stats.slotCounts[s]));
  const maxTierSlotCell = Math.max(
    1,
    ...stats.realTiers.flatMap(t => stats.slotLabels.map(s => stats.tierSlot[t][s] || 0))
  );

  const pct = (n) => (stats.total ? Math.round((n / stats.total) * 100) : 0);

  return (
    <div className="space-y-5">
      {/* Headline numbers */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total players" value={stats.total} />
        <Stat
          label="Assigned to a group"
          value={`${stats.assigned}`}
          sub={`${pct(stats.assigned)}% · ${stats.total - stats.assigned} unassigned`}
        />
        <Stat label="Avg. availability" value={stats.avgSlots.toFixed(1)} sub="slots / player per week" />
        <Stat label="Busiest slot" value={stats.busiestSlot ? stats.busiestSlot.replace(/:00/g, "") : "—"} sub={stats.busiestSlot ? `${stats.slotCounts[stats.busiestSlot]} players free` : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Tier distribution */}
        <Card title="Players by tier" subtitle="How the roster splits across skill tiers">
          <div className="space-y-1.5">
            {stats.realTiers.map(tier => {
              const n = stats.tierCounts[tier];
              const colors = TIER_COLORS[tier] || {};
              return (
                <div key={tier} className="flex items-center gap-2 text-xs">
                  <span className="w-40 shrink-0 text-gray-600 truncate" title={tier}>{tier}</span>
                  <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                    <div
                      className={`h-full ${colors.header || "bg-gray-300"} rounded`}
                      style={{ width: `${(n / maxTier) * 100}%` }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-gray-500 tabular-nums">
                    {n} · {pct(n)}%
                  </span>
                </div>
              );
            })}
            {stats.untiered > 0 && (
              <div className="text-[11px] text-gray-400 pt-1">{stats.untiered} player(s) with an unrecognized tier</div>
            )}
          </div>
        </Card>

        {/* Classification donut-ish split */}
        <Card title="Junior vs. adult" subtitle="Classification breakdown">
          <div className="space-y-1.5">
            {Object.entries(stats.classCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([cls, n]) => (
                <div key={cls} className="flex items-center gap-2 text-xs">
                  <span className="w-24 shrink-0 text-gray-600">{cls}</span>
                  <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                    <div
                      className="h-full bg-blue-400 rounded"
                      style={{ width: `${pct(n)}%` }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-gray-500 tabular-nums">{n} · {pct(n)}%</span>
                </div>
              ))}
          </div>
          {stats.juniorCount > 0 && (
            <>
              <h4 className="text-xs font-semibold text-gray-500 mt-4 mb-2">Junior ages</h4>
              <div className="flex items-end gap-2 h-24">
                {Object.entries(stats.ageBuckets).map(([bucket, n]) => {
                  const maxAge = Math.max(1, ...Object.values(stats.ageBuckets));
                  return (
                    <div key={bucket} className="flex-1 flex flex-col items-center justify-end gap-1">
                      <span className="text-[11px] text-gray-500 tabular-nums">{n}</span>
                      <div
                        className="w-full bg-teal-300 rounded-t"
                        style={{ height: `${(n / maxAge) * 100}%`, minHeight: n > 0 ? "4px" : "0" }}
                      />
                      <span className="text-[10px] text-gray-400">{bucket}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>

        {/* Availability by day */}
        <Card title="Availability by day" subtitle="Total player-slots offered each weekday">
          <div className="flex items-end gap-3 h-32">
            {DAYS.map(day => (
              <div key={day} className="flex-1 flex flex-col items-center justify-end gap-1">
                <span className="text-[11px] text-gray-500 tabular-nums">{stats.dayCounts[day]}</span>
                <div
                  className="w-full bg-indigo-300 rounded-t"
                  style={{ height: `${(stats.dayCounts[day] / maxDay) * 100}%`, minHeight: stats.dayCounts[day] > 0 ? "4px" : "0" }}
                />
                <span className="text-[10px] text-gray-400">{day.slice(0, 3)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Availability by time slot */}
        <Card title="Availability by time" subtitle="When players say they can play">
          <div className="space-y-1.5">
            {stats.slotLabels.map(slot => {
              const n = stats.slotCounts[slot];
              return (
                <div key={slot} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 text-gray-600 tabular-nums">{slot}</span>
                  <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded"
                      style={{ width: `${(n / maxSlot) * 100}%` }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right text-gray-500 tabular-nums">{n}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Tier x time heatmap */}
      <Card title="Availability heatmap" subtitle="Darker = more players of that tier free in that slot">
        <div className="overflow-x-auto">
          <table className="text-[11px] border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white text-left font-medium text-gray-400 pr-2 pb-1">Tier \ Time</th>
                {stats.slotLabels.map(s => (
                  <th key={s} className="px-1 pb-1 font-medium text-gray-400 whitespace-nowrap text-center">
                    {s.replace(/:00/g, "").replace(/ ?[AP]M/g, m => m.trim().toLowerCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.realTiers.map(tier => (
                <tr key={tier}>
                  <td className="sticky left-0 bg-white text-gray-600 pr-2 py-0.5 whitespace-nowrap" title={tier}>{tier}</td>
                  {stats.slotLabels.map(slot => {
                    const n = stats.tierSlot[tier][slot] || 0;
                    const intensity = n / maxTierSlotCell;
                    return (
                      <td key={slot} className="p-0.5">
                        <div
                          className="w-8 h-6 rounded flex items-center justify-center tabular-nums"
                          style={{
                            backgroundColor: n === 0 ? "#f3f4f6" : `rgba(16, 185, 129, ${0.15 + intensity * 0.85})`,
                            color: intensity > 0.55 ? "#fff" : "#374151",
                          }}
                          title={`${tier} · ${slot}: ${n}`}
                        >
                          {n || ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
